"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "antd/es/button";
import Flex from "antd/es/flex";
import Form from "antd/es/form";
import Typography from "antd/es/typography";
import Text from "antd/es/typography/Text";
import { ComponentProps } from "./types";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import { extractErrorMessage } from "@/lib/helpers/error";
import InlineError from "./components/InlineError";
import InlineNotice from "./components/InlineNotice";
import VerificationResendButton from "./components/VerificationResendButton";
import VerificationCodeInput from "./components/VerificationCodeInput";
import { MODAL_COMPONENT_SIZE } from "./consts";
import { useLocale } from "@/lib/i18n";
import { useResendCooldown } from "./useResendCooldown";
import { isVerificationCodeAlreadySentError } from "./verificationSendError";
import styles from "./appointmentFlow.module.css";

const buildBookingPurpose = (salonId: string) => `booking:${salonId}`;

const VerifyRepeatClient: React.FC<ComponentProps> = ({
  appointmentDetails,
  state,
  onCreateAppointment,
}) => {
  const { t } = useLocale();
  const {
    isCoolingDown: isResendCoolingDown,
    startCooldown: startResendCooldown,
  } = useResendCooldown();
  const { phoneNumber, email, customerId, paymentMethodId } = state.state;
  const verificationMethod = appointmentDetails.verificationMethod ?? "SMS";
  const isEmail = verificationMethod === "EMAIL";
  const [code, setCode] = useState<string>("");
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSending, setSending] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [codeAlreadySent, setCodeAlreadySent] = useState(false);
  const initialSendRef = useRef(false);

  // Every successful send starts the cooldown, the automatic one included:
  // the server refuses another send for 45s, so an enabled Re-send could only
  // fail (BF-DRIFT-010).
  const sendVerificationCode = useCallback(async () => {
    if (isEmail) {
      if (!email) return;
    } else {
      if (!phoneNumber) return;
    }
    const purpose = buildBookingPurpose(appointmentDetails.salonId);
    setErrorMessage(null);
    setSending(true);
    try {
      await bookingFlowApi.sendVerification(
        isEmail ? { email, purpose } : { phoneNumber: phoneNumber!, purpose },
      );
      setCodeAlreadySent(false);
      startResendCooldown();
    } catch (err: any) {
      // The code the customer already has still works, so this is recovery,
      // not a dead end: hold the same cooldown and say which code to enter.
      if (isVerificationCodeAlreadySentError(err)) {
        setCodeAlreadySent(true);
        startResendCooldown();
        return;
      }
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  }, [
    appointmentDetails.salonId,
    phoneNumber,
    email,
    isEmail,
    startResendCooldown,
  ]);

  // Auto-send on mount for both SMS and email
  useEffect(() => {
    if (initialSendRef.current) return;
    const identifier = isEmail ? email : phoneNumber;
    if (!identifier) return;
    initialSendRef.current = true;
    void sendVerificationCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, email, isEmail]);

  const handleSubmit = useCallback(async () => {
    if (!customerId || !code) {
      if (!code) setCodeError(true);
      return;
    }
    setCodeError(false);
    setSubmitting(true);
    onCreateAppointment({
      customerId,
      paymentMethodId,
      verificationCode: code,
      errorCallback: () => setSubmitting(false),
    });
  }, [code, customerId, onCreateAppointment, paymentMethodId]);

  return (
    <div className={styles.stepContainer}>
      <Typography.Paragraph className={styles.prompt}>
        {isEmail
          ? (t.verificationEmailSentMessage ??
            "A verification code has been sent to your email.")
          : t.verifyPhoneMessage}
      </Typography.Paragraph>

      <InlineNotice
        message={codeAlreadySent ? t.verificationAlreadySent : null}
      />
      <Form layout="vertical" className={styles.compactForm}>
        <Form.Item
          label={<Text strong>{t.verificationCode}</Text>}
          htmlFor="code"
        >
          <VerificationCodeInput
            id="code"
            size={MODAL_COMPONENT_SIZE}
            placeholder={t.codePlaceholder}
            value={code}
            status={codeError ? "error" : ""}
            onChange={(digits) => {
              setCode(digits);
              if (codeError) setCodeError(false);
            }}
          />
        </Form.Item>
      </Form>

      <InlineError message={errorMessage} />

      <div className={styles.actionRow}>
        <Flex vertical gap={8}>
          <Button
            type="primary"
            size={MODAL_COMPONENT_SIZE}
            loading={isSubmitting}
            disabled={isSubmitting || !code}
            onClick={handleSubmit}
            block
          >
            {t.submit}
          </Button>
          <VerificationResendButton
            isCoolingDown={isResendCoolingDown}
            isSending={isSending}
            isSubmitting={isSubmitting}
            onResend={() => void sendVerificationCode()}
          />
        </Flex>
      </div>
    </div>
  );
};

export default VerifyRepeatClient;
